import { TitleCasePipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";
import { FieldTree } from "@angular/forms/signals";
import { StrategyDspAudiencePreset, StrategyDspAudienceType, StrategyDspFunnel } from "@front/m19-api-client";
import { AudienceId, DspAudiencesConfig } from "@front/m19-services";
import { ModalService } from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import {
  ManualAudienceModalComponent,
  ManualAudienceModalData,
  ManualAudienceModalResult,
} from "@m19-board/dsp/manual-audience-modal/manual-audience-modal.component";
import { DspAudiencesSelectorComponent } from "@m19-board/dsp/dsp-audiences-selector/dsp-audiences-selector.component";
import { DspSmartAudienceSelectorComponent } from "@m19-board/dsp/dsp-smart-audience-selector/dsp-smart-audience-selector.component";

export interface DspCreateStrategyAudiencesModel {
  audiencePreset: StrategyDspAudiencePreset;
  audiences: AudienceId[][];
  blacklistedAudiences: AudienceId[][];
}

@Component({
  selector: "app-dsp-create-strategy-audiences-step",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoDirective, TitleCasePipe, DspAudiencesSelectorComponent, DspSmartAudienceSelectorComponent],
  templateUrl: "./dsp-create-strategy-audiences-step.component.html",
})
export class DspCreateStrategyAudiencesStepComponent {
  private readonly modalService = inject(ModalService);
  private readonly translationService = inject(TranslocoService);

  readonly audiences = input.required<FieldTree<DspCreateStrategyAudiencesModel>>();
  readonly funnel = input.required<StrategyDspFunnel | null>();

  readonly StrategyDspAudienceType = StrategyDspAudienceType;

  readonly isAwareness = computed(() => this.funnel() === StrategyDspFunnel.AWARENESS);

  readonly isManualPreset = computed(
    () => this.audiences().audiencePreset().value() === StrategyDspAudiencePreset.MANUAL,
  );

  readonly existingAudienceConfig = computed<DspAudiencesConfig | undefined>(() => ({
    whitelistAudiences: this.audiences().audiences().value(),
    blacklistAudiences: this.audiences().blacklistedAudiences().value(),
  }));

  selectAudiencePreset(audiencePreset: StrategyDspAudiencePreset) {
    this.audiences().audiencePreset().markAsDirty();
    this.audiences().audiencePreset().value.set(audiencePreset);
    this.audiences().audiences().value.set([]);
    this.audiences().blacklistedAudiences().value.set([]);
  }

  selectAudiences(value: AudienceId[][]) {
    this.audiences().audiences().markAsDirty();
    this.audiences().audiences().value.set(value);
  }

  selectBlacklistedAudiences(value: AudienceId[][]) {
    this.audiences().blacklistedAudiences().markAsDirty();
    this.audiences().blacklistedAudiences().value.set(value);
  }

  selectManualAudiences(creation: boolean = false) {
    if (this.isManualPreset() && creation) return;
    this.audiences().audiencePreset().markAsDirty();
    this.modalService
      .openModal<ManualAudienceModalData, ManualAudienceModalResult>(ManualAudienceModalComponent, {
        modalTitle: creation
          ? this.translationService.translate("dsp-audiences-selector.create_audiences")
          : this.translationService.translate("dsp-audiences-selector.manage_audiences"),
        data: {
          mode: creation ? "creation" : "edit",
          isAudienceRequired: this.funnel() !== StrategyDspFunnel.AWARENESS,
          whitelistAudiences: this.audiences().audiences().value(),
          blacklistAudiences: this.audiences().blacklistedAudiences().value(),
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.audiences().audiences().value.set(result.whitelistAudiences);
          this.audiences().blacklistedAudiences().value.set(result.blacklistAudiences);
          this.audiences().audiencePreset().value.set(StrategyDspAudiencePreset.MANUAL);
        }
      });
  }
}
